import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { Badge } from '../../components/ui/Badge';
import { listAuditLogs, type AuditLog } from '../../api/admin';
import { FileText, User, Building2 } from 'lucide-react';

const actionLabels: Record<string, string> = {
  'plan_order.confirm': '确认套餐订单',
  'plan_order.create': '创建套餐订单',
  'subscription.extend': '延长订阅',
  'shop.create': '创建门店',
  'shop.update': '更新门店',
  'staff.add': '添加员工',
  'staff.remove': '移除员工',
};

function AuditLogRow({ item }: { item: AuditLog }) {
  const { log, actor, shop } = item;
  return (
    <tr className="text-sm">
      <td className="py-3">
        <div className="font-medium text-[#1E293B]">
          {actionLabels[log.action] || log.action}
        </div>
        <div className="mt-0.5 text-xs text-[#94A3B8]">{log.action}</div>
      </td>
      <td className="py-3">
        <div className="flex items-center gap-1 text-[#64748B]">
          <User className="h-4 w-4" />
          {actor?.displayName || actor?.phone || '-'}
        </div>
      </td>
      <td className="py-3">
        {shop ? (
          <div className="flex items-center gap-1 text-[#64748B]">
            <Building2 className="h-4 w-4" />
            {shop.name}
          </div>
        ) : (
          <span className="text-[#94A3B8]">-</span>
        )}
      </td>
      <td className="py-3">
        <Badge variant="default">{log.targetType}</Badge>
      </td>
      <td className="py-3 text-[#94A3B8]">
        {new Date(log.createdAt).toLocaleString('zh-CN')}
      </td>
    </tr>
  );
}

export function AuditLogsPage(): React.JSX.Element {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['admin', 'audit-logs'],
    queryFn: () => listAuditLogs({ pageSize: 50 }),
  });

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-[#1E293B]">操作日志</h1>
        <p className="mt-1 text-sm text-[#64748B]">
          平台敏感操作审计记录
        </p>
      </div>

      {/* 日志列表 */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            审计日志
            <span className="text-sm font-normal text-[#64748B]">
              共 {data?.total ?? 0} 条
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="py-12 text-center text-sm text-[#94A3B8]">加载中...</div>
          ) : isError ? (
            <div className="py-12 text-center text-sm text-[#DC2626]">加载失败，请刷新重试</div>
          ) : !data?.rows.length ? (
            <div className="py-12 text-center">
              <FileText className="mx-auto h-12 w-12 text-[#E2E8F0]" />
              <p className="mt-3 text-sm text-[#94A3B8]">暂无审计日志</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-[#E2E8F0] text-left text-sm text-[#64748B]">
                    <th className="pb-3 font-medium">操作</th>
                    <th className="pb-3 font-medium">操作人</th>
                    <th className="pb-3 font-medium">关联门店</th>
                    <th className="pb-3 font-medium">目标类型</th>
                    <th className="pb-3 font-medium">时间</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#F1F5F9]">
                  {data.rows.map((item) => (
                    <AuditLogRow key={item.log.id} item={item} />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
